<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between mb-5">
      <div>
        <h2 class="text-h5 font-weight-semibold mb-1">Statistikat e demos</h2>
        <p class="text--secondary mb-0">
          {{ $t('pages.demo_usage_intro') }}
        </p>
      </div>

      <v-select
        v-model="days"
        :items="periudhat"
        item-text="text"
        item-value="value"
        dense
        outlined
        hide-details
        style="max-width: 190px"
        @change="merr"
      ></v-select>
    </div>

    <v-progress-linear v-if="loading" indeterminate class="mb-4"></v-progress-linear>

    <template v-if="data">
      <!-- Numrat kryesore -->
      <v-row class="mb-1">
        <v-col v-for="k in kartat" :key="k.titulli" cols="12" sm="6" md="3">
          <v-card class="pa-4" outlined>
            <div class="d-flex align-center mb-2">
              <v-avatar :color="k.ngjyra" size="38" class="me-3">
                <v-icon dark size="20">{{ k.ikona }}</v-icon>
              </v-avatar>
              <span class="text--secondary text-caption">{{ k.titulli }}</span>
            </div>
            <div class="text-h4 font-weight-bold">{{ k.vlera }}</div>
            <div class="text-caption text--secondary">{{ k.nen }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <!-- Nga cila pamje hyne -->
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4" height="100%">
            <div class="font-weight-semibold mb-3">Sipas pamjes</div>
            <div v-if="!Object.keys(data.by_view).length" class="text--secondary text-caption">
              {{ $t('messages.no_visits_yet') }}
            </div>
            <div
              v-for="(n, pamja) in data.by_view"
              :key="pamja"
              class="d-flex align-center justify-space-between mb-2"
            >
              <span>{{ pamja }}</span>
              <v-chip small color="primary" outlined>{{ n }}</v-chip>
            </div>
          </v-card>
        </v-col>

        <!-- Prej nga vijne -->
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4" height="100%">
            <div class="font-weight-semibold mb-3">Vendndodhjet kryesore</div>
            <div v-if="!Object.keys(data.locations).length" class="text--secondary text-caption">
              {{ $t('messages.no_locations_yet') }}
            </div>
            <div
              v-for="(n, vendi) in data.locations"
              :key="vendi"
              class="d-flex align-center justify-space-between mb-2"
            >
              <span class="text-truncate me-2">{{ vendi }}</span>
              <v-chip small outlined>{{ n }}</v-chip>
            </div>
          </v-card>
        </v-col>

        <!-- Cka i terhoqi -->
        <v-col cols="12" md="4">
          <v-card outlined class="pa-4" height="100%">
            <div class="font-weight-semibold mb-3">{{ $t('pages.most_visited') }}</div>
            <div v-if="!Object.keys(data.modules).length" class="text--secondary text-caption">
              {{ $t('messages.no_sections_opened_yet') }}
            </div>
            <div
              v-for="(n, moduli) in data.modules"
              :key="moduli"
              class="d-flex align-center justify-space-between mb-2"
            >
              <span class="text-truncate me-2">{{ moduli }}</span>
              <v-chip small color="success" outlined>{{ n }}</v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Vizitat nje nga nje -->
      <v-card outlined class="mt-5">
        <v-card-title class="text-subtitle-1 font-weight-semibold">
          Vizitat
          <span class="text-caption text--secondary ms-2">{{ data.visits.length }} gjithsej</span>
        </v-card-title>
        <v-data-table
          :headers="kolonatVizita"
          :items="data.visits"
          :items-per-page="20"
          :footer-props="{ 'items-per-page-options': [20, 50, 100, -1] }"
          dense
        >
          <template v-slot:item.started_at="{ item }">
            {{ dataOra(item.started_at) }}
          </template>
          <template v-slot:item.location="{ item }">
            <span v-if="item.location">{{ item.location }}</span>
            <span v-else class="text--disabled text-caption">e panjohur</span>
          </template>
          <template v-slot:item.modules="{ item }">
            <span v-if="!item.modules.length" class="text--disabled text-caption">-</span>
            <v-chip
              v-for="m in item.modules"
              :key="m"
              x-small
              outlined
              class="me-1 mb-1"
            >{{ m }}</v-chip>
          </template>
          <template v-slot:item.identified="{ item }">
            <v-icon v-if="item.identified" small color="success">mdi-check-circle</v-icon>
            <span v-else class="text--disabled">-</span>
          </template>
        </v-data-table>
      </v-card>

      <!-- Kush la te dhenat -->
      <v-card outlined class="mt-5">
        <v-card-title class="text-subtitle-1 font-weight-semibold">
          Kontaktet nga demoja
        </v-card-title>
        <v-card-subtitle class="pb-2">
          Vizitorët që lanë të dhënat për të vazhduar pas tre minutash. Të verifikuarit e konfirmuan email-in me kod.
        </v-card-subtitle>
        <v-data-table
          :headers="kolonatKontakte"
          :items="data.leads"
          :items-per-page="20"
          :no-data-text="$t('messages.no_visits_yet')"
          dense
        >
          <template v-slot:item.created_at="{ item }">
            {{ dataOra(item.created_at) }}
          </template>
          <template v-slot:item.phone="{ item }">
            <a :href="'tel:' + item.phone" class="font-weight-semibold">{{ item.phone }}</a>
          </template>
          <template v-slot:item.email="{ item }">
            <a v-if="item.email" :href="'mailto:' + item.email">{{ item.email }}</a>
            <span v-else>—</span>
          </template>
          <template v-slot:item.verified="{ item }">
            <v-chip x-small :color="item.verified ? 'success' : 'grey'" text-color="white">
              {{ item.verified ? 'Po' : 'Jo' }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card>

      <p class="text-caption text--secondary mt-4">
        Regjistrohen vetëm veprimet e llogarive demo. Klientët realë nuk gjurmohen.
      </p>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DemoStatistics',

  data() {
    return {
      loading: true,
      data: null,
      days: 30,
      periudhat: [
        { text: '7 ditët e fundit', value: 7 },
        { text: '30 ditët e fundit', value: 30 },
        { text: '90 ditët e fundit', value: 90 },
        { text: 'Një vit', value: 365 },
      ],
      kolonatVizita: [
        { text: 'Kur', value: 'started_at' },
        { text: 'Pamja', value: 'view' },
        { text: 'Vendndodhja', value: 'location' },
        { text: 'Pajisja', value: 'device' },
        { text: 'Zgjati', value: 'duration' },
        { text: 'Veprime', value: 'actions' },
        { text: 'Pjesët e hapura', value: 'modules', sortable: false },
        { text: 'La të dhënat', value: 'identified' },
      ],
      kolonatKontakte: [
        { text: 'Kur', value: 'created_at' },
        { text: 'Emri', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Telefoni', value: 'phone' },
        { text: 'Verifikuar', value: 'verified' },
        { text: 'Pamja', value: 'view' },
        { text: 'Vendndodhja', value: 'location' },
      ],
    };
  },

  computed: {
    kartat() {
      const t = this.data.totals;

      return [
        {
          titulli: 'Vizita',
          vlera: t.visits,
          nen: t.all_time + ' që nga fillimi',
          ikona: 'mdi-account-multiple',
          ngjyra: 'primary',
        },
        {
          titulli: 'Vendndodhje',
          vlera: t.locations,
          nen: 'qytete të ndryshme',
          ikona: 'mdi-map-marker',
          ngjyra: 'info',
        },
        {
          titulli: 'Kohë mesatare',
          vlera: t.avg_duration,
          nen: 'për vizitë me veprime',
          ikona: 'mdi-clock-outline',
          ngjyra: 'success',
        },
        {
          titulli: 'Kontakte',
          vlera: t.leads,
          nen: (t.verified || 0) + ' me email të verifikuar',
          ikona: 'mdi-phone',
          ngjyra: 'warning',
        },
      ];
    },
  },

  created() {
    this.merr();
  },

  methods: {
    async merr() {
      this.loading = true;

      try {
        const res = await axios.get('/auth/demo/leads', { params: { days: this.days } });
        this.data = res.data;
      } catch (e) {
        this.data = null;
      } finally {
        this.loading = false;
      }
    },

    /** Data dhe ora si i lexon njeriu, jo si i ruan baza. */
    dataOra(v) {
      if (!v) return '-';

      const d = new Date(v);
      const dy = (n) => String(n).padStart(2, '0');

      return dy(d.getDate()) + '/' + dy(d.getMonth() + 1) + '/' + d.getFullYear()
        + ' ' + dy(d.getHours()) + ':' + dy(d.getMinutes());
    },
  },
};
</script>
